import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Megaphone, Check, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useFirma } from "../context/FirmaContext";
import { useAuth } from "../context/AuthContext";

const PRIO = { urgente: "#dc2626", importante: "#d97706", normale: "#1f7a5c" };

export default function NewsPopup() {
  const { activeOperatore } = useFirma();
  const { selectedStructure } = useAuth();
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const lastOpRef = useRef(null);

  useEffect(() => {
    const op = activeOperatore;
    if (!op || !op.id) { lastOpRef.current = null; return; }
    if (lastOpRef.current === op.id) return; // già caricato per questo operatore
    lastOpRef.current = op.id;
    const params = { operatore_id: op.id };
    if (selectedStructure && selectedStructure !== "all") params.structure_id = selectedStructure;
    api.get("/news/popup", { params })
      .then((r) => { setQueue(r.data || []); setIdx(0); })
      .catch(() => setQueue([]));
  }, [activeOperatore, selectedStructure]);

  const current = queue[idx];
  if (!current) return null;

  const op = activeOperatore;
  const isConferma = (current.popup_modo || "una_volta") === "conferma";
  const isUrgent = current.priorita === "urgente";
  const isHigh = isUrgent || current.priorita === "importante";
  const col = isUrgent ? "#dc2626" : (current.priorita === "importante" ? "#ea580c" : (PRIO[current.priorita] || PRIO.normale));
  const accentBorder = isUrgent ? "border-red-600" : "border-orange-400";
  const badgeBg = isUrgent ? "bg-red-600" : "bg-orange-500";
  const titleCol = isUrgent ? "text-red-700" : "text-orange-700";
  const btnCol = isUrgent ? "bg-red-600 hover:bg-red-700" : "bg-orange-500 hover:bg-orange-600";

  const dismiss = async (confirmed) => {
    try {
      await api.post(`/news/${current.id}/popup-visto`, {
        operatore_id: op?.id, operatore_nome: op ? `${op.nome} ${op.cognome}`.trim() : "", conferma: !!confirmed,
      });
    } catch { /* noop */ }
    if (idx + 1 < queue.length) setIdx(idx + 1);
    else { setQueue([]); setIdx(0); }
  };

  // Il pop-up è bloccante: non si chiude cliccando fuori, con Esc o con la X.
  // L'unico modo per proseguire è cliccare "Ho capito".
  return (
    <Dialog open={!!current} onOpenChange={() => { /* chiusura solo dal pulsante */ }}>
      <DialogContent
        className={`${isHigh ? `max-w-2xl sm:max-w-3xl border-4 ${accentBorder}` : "max-w-md"} max-h-[90vh] overflow-y-auto [&>button]:hidden`}
        data-testid="news-popup-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {isHigh && (
          <div className={`flex items-center gap-2 rounded-lg ${badgeBg} text-white px-3 py-2 font-bold uppercase tracking-wide text-sm`} data-testid="news-popup-priorita-badge">
            <AlertTriangle size={18} /> {isUrgent ? "Avviso urgente" : "Avviso importante"}
          </div>
        )}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-grid place-items-center rounded-full text-white shrink-0" style={{ background: col, height: isHigh ? 48 : 36, width: isHigh ? 48 : 36 }}><Megaphone size={isHigh ? 26 : 18} /></span>
            <span data-testid="news-popup-titolo" className={isHigh ? `text-2xl sm:text-3xl font-extrabold ${titleCol}` : ""}>{current.titolo}</span>
          </DialogTitle>
        </DialogHeader>
        {queue.length > 1 && <div className="text-[11px] font-semibold text-muted-foreground -mt-2">Avviso {idx + 1} di {queue.length}</div>}
        {current.testo && <div className={`whitespace-pre-wrap ${isHigh ? "text-lg text-slate-800 font-medium" : "text-sm text-slate-700"}`} data-testid="news-popup-testo">{current.testo}</div>}
        <DialogFooter>
          <Button onClick={() => dismiss(isConferma)} data-testid={isConferma ? "news-popup-ho-capito" : "news-popup-chiudi"} className={`w-full ${isHigh ? `h-14 text-lg ${btnCol}` : ""}`}>
            <Check size={isHigh ? 20 : 16} className="mr-1.5" /> Ho capito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
